import { IProduct } from '@components/utils/model/product.model'
import { useEffect, useState } from 'react'
import { IProductCorrectStockPerAttribute } from '../AttributesHandlerProductView'
import {
  API_STATUS,
  EmptyGuid,
  NEXT_CREATE_WISHLIST,
} from '@components/utils/constants'
import { groupBy, round } from '@framework/utils/lodash'
import { useUI } from '@components/ui'
import { GA_EVENT } from 'hooks/ga_event.modal'
import { getSizeChartData, openLoginView } from '@framework/utils/app-util'
import { recordGA4Event } from '@components/services/analytics/ga4'
import { useMoEngageEvent } from 'hooks/useMoengageEvent'
import { MO_ENGAGE_EVENT } from 'hooks/moengage_event.modal'
import { ADDED_TO_WISH } from '@components/utils/textVariables'
import axios from '@services/axiosInterceptorInstance'
import { useRouter } from 'next/router'

const separatorSign = '#'

export interface ISizeChart {
  Sizes: any
  Images: any
  attribute: any
  SizesToDisplay: any
  SizeKeyToDisplay: any
}

interface IProps {
  product: IProduct
  currentVariantData: any
  fieldCode: string
  currentAttribute: string | undefined
  selectedSizeProduct: any
  currentPage: string
  isMobile: boolean
  productId: string
  window: any
  items: any
  setQuickview: any
  onChange: (fieldCode: string, value: string, obj?: any) => void
  getStockPerAttribute: (
    key: string,
    variant: string
  ) => IProductCorrectStockPerAttribute
}

const useSizeLineHook = ({
  product,
  items,
  fieldCode,
  currentAttribute,
  currentVariantData,
  selectedSizeProduct,
  currentPage,
  isMobile,
  productId,
  window,
  setQuickview,
  onChange,
  getStockPerAttribute,
}: IProps) => {
  const { user, setSidebarView, addToWishlist, wishListItems, setAlert } =
    useUI()
  const trackMoEngageEvent = useMoEngageEvent()
  const { asPath } = useRouter()
  const [productData, setProductData] = useState(
    getStockPerAttribute(
      fieldCode,
      currentVariantData ? currentVariantData : currentAttribute
    )
  )
  const [isSizeChart, setSizeChart] = useState(false)
  const [enabled, setEnabled] = useState(false)
  const [isSizeChange, setSizeChange] = useState(false)
  const [SizeChart, setSizeChartData] = useState<ISizeChart>({
    Sizes: [],
    Images: [],
    attribute: [],
    SizesToDisplay: [],
    SizeKeyToDisplay: [],
  })
  const [selected, setSelected] = useState({
    ...product,
    stock: productData.currentStock,
    productId: productData.productId,
    stockCode: productData.stockCode,
    currentAttribute: currentVariantData
      ? currentVariantData
      : currentAttribute,
  })
  const [isImageLoaded, setIsImageLoaded] = useState(true)
  const [isSizeChartEnable, setSizeChartEnable] = useState(
    API_STATUS.NOT_STARTED
  )

  const isPreOrderEnabled = productData.isPreOrderEnabled
  const saving = product?.listPrice?.raw?.withTax - product?.price?.raw?.withTax
  const discount = round((saving / product?.listPrice?.raw?.withTax) * 100, 0)
  const selectedSizeInLineProduct = selectedSizeProduct ?? product
  const selectedSizeInLineProductSaving =
    selectedSizeInLineProduct?.listPrice?.raw?.withTax -
    selectedSizeInLineProduct?.price?.raw?.withTax
  const selectedSizeInLineProductDiscount = round(
    (selectedSizeInLineProductSaving / product?.listPrice?.raw?.withTax) * 100,
    0
  )
  const isInWishList = wishListItems
    ?.map((wi: any) => wi?.stockCode)
    ?.includes(product?.stockCode)
  const attrGroup = groupBy(product?.customAttributes, 'key')
  const isPDPVisible = product?.price?.raw?.withTax !== 0

  function sizeChart() {
    recordGA4Event(window, GA_EVENT.SIZE_CHART, {
      product_name: product?.name,
      current_page: currentPage,
      device: isMobile ? 'Mobile web' : 'Desktop web',
      category: product?.classification?.mainCategoryName,
      loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
    })

    setSizeChart(true)
  }

  useEffect(() => {
    const getStockPerAttrData = getStockPerAttribute(
      fieldCode,
      currentVariantData ? currentVariantData : currentAttribute
    )
    setProductData(getStockPerAttrData)
    setSelected({
      ...product,
      stock: getStockPerAttrData.currentStock,
      productId: getStockPerAttrData.productId,
      stockCode: getStockPerAttrData.stockCode,
      currentAttribute: currentVariantData
        ? currentVariantData
        : currentAttribute,
    })
  }, [currentVariantData, currentAttribute])

  const getSizeChart = async () => {
    setSizeChartEnable(API_STATUS.LOADING)
    const data: any = await getSizeChartData(
      product?.mappedCategories?.[0]?.categoryName
    )
    const type = typeof data
    const _data = type === 'string' ? JSON.parse(data) : data
    if (_data) {
      let sizesToDisplay: Array<string> = []
      let sizesKeyToDisplay: Array<string> = []
      const nonEmptyKeys = _data?.Sizes?.reduce((result: any, obj: any) => {
        Object.keys(obj).forEach((key) => {
          if (obj[key]) {
            result.add(key)
          }
        })
        return result
      }, new Set())

      _data?.Sizes?.forEach((item: any, id: any) => {
        if (id === 0) {
          const fullKeys = Array.from(nonEmptyKeys)
          const d = fullKeys?.filter(
            (i: any) => i !== 'Size' && i !== 'CategoryName'
          )
          const _d = d?.map((_i: any) => {
            const size = _i?.replace(/(Inches|Cms)$/, '')
            sizesKeyToDisplay?.push(size)
            return size?.replace(/([a-z])([A-Z])/g, '$1 $2')
          })
          const uniqueSizesKeyToDisplay = [...new Set(sizesKeyToDisplay)]
          const uniqueSizesToDisplay = [...new Set(_d)]
          sizesToDisplay = uniqueSizesToDisplay
          sizesKeyToDisplay = uniqueSizesKeyToDisplay
        }
      })
      setSizeChartData({
        ..._data,
        SizesToDisplay: sizesToDisplay,
        SizeKeyToDisplay: sizesKeyToDisplay,
      })
      setSizeChartEnable(API_STATUS.SUCCESS)
    } else {
      setSizeChartEnable(API_STATUS.FAILURE)
    }
  }

  useEffect(() => {
    const getStockPerAttrData = getStockPerAttribute(
      fieldCode,
      currentVariantData ? currentVariantData : currentAttribute
    )
    setProductData(getStockPerAttrData)
    setSelected({
      ...product,
      stock: getStockPerAttrData.currentStock,
      productId: getStockPerAttrData.productId,
      stockCode: getStockPerAttrData.stockCode,
      currentAttribute: currentVariantData
        ? currentVariantData
        : currentAttribute,
    })
    if (product) {
      getSizeChart()
    }
  }, [productId])

  const generateItemOption = (value: any, stock: number) => {
    if (stock <= 0 && !isPreOrderEnabled)
      return `${value.toUpperCase()} ${separatorSign} Sold Out`
    if (stock <= 0 && isPreOrderEnabled) {
      return `${value.toUpperCase()} ${separatorSign} PRE-ORDER`
    }
    if ((stock < 5 && stock > 0) || isPreOrderEnabled) {
      return `${value?.toUpperCase()} ${separatorSign} ${stock} LEFT`
    }
    // else {
    //   return `${value?.toUpperCase()} - ${stock} LEFT`
    // }
    return value?.toUpperCase()
  }
  const handleChange = (item: any) => {
    recordGA4Event(window, GA_EVENT.SIZE_CHANGE, {
      category: product?.mappedCategories.length
        ? product?.mappedCategories[product?.mappedCategories.length - 1]
            ?.categoryName
        : '',
      category2: product?.mappedCategories[2]?.categoryName,
      size_clicked: item?.fieldValue,
      product_name: product?.name,
      device: isMobile ? 'Mobile web' : 'Desktop web',
      current_page: currentPage,
      loggedin_status: !!(user?.userId && user?.userId !== EmptyGuid),
    })

    trackMoEngageEvent(
      MO_ENGAGE_EVENT.SIZES,
      {
        URL: window?.location?.href,
        label: item?.fieldValue,
        action: 'Click',
        size_clicked: item?.fieldValue,
        Product_name: product?.name,
      },
      window
    )
    return onChange(fieldCode, item?.fieldValue, item)
  }
  const handleChangeFromSizeChart = (size: any) => {
    return items.filter(
      (i: any) => i.fieldValue?.toUpperCase() === size?.toUpperCase()
    )[0]
  }

  function setEnable() {
    setSizeChange(true)
    setEnabled(true)
  }
  function setDisabled() {
    setSizeChange(false)
    setEnabled(false)
  }

  const sanitizeSize = (value: string) => {
    if (value) {
      return value?.toUpperCase()?.replaceAll('T', '')?.replaceAll('V', '')
    }
    return value
  }

  const insertToLocalWishlist = () => {
    addToWishlist(product)
    setAlert({ type: 'success', msg: ADDED_TO_WISH })
    setSizeChart(false)
    setQuickview(undefined)
    // openWishlist()
  }

  const handleWishList = () => {
    const accessToken = localStorage?.getItem('user')
    if (accessToken) {
      const createWishlist = async () => {
        try {
          await axios.post(NEXT_CREATE_WISHLIST, {
            id: user.userId,
            productId: product.recordId,
            flag: true,
          })
          insertToLocalWishlist()
        } catch (error) {
          console.error(':: error ', error)
        }
      }
      createWishlist()
    } else {
      setSizeChart(false)
      setQuickview(undefined)
      openLoginView(asPath)
      setSidebarView('LOGIN_VIEW')
      // insertToLocalWishlist()
    }
  }
  const isVisible = () => {
    return isPDPVisible && product?.isVisible
  }

  return {
    isSizeChart,
    enabled,
    isSizeChange,
    SizeChart,
    selected,
    isImageLoaded,
    isSizeChartEnable,
    discount,
    selectedSizeInLineProductDiscount,
    selectedSizeInLineProduct,
    isInWishList,
    attrGroup,
    handleWishList,
    sanitizeSize,
    isVisible,
    generateItemOption,
    handleChangeFromSizeChart,
    handleChange,
    sizeChart,
    setIsImageLoaded,
    setEnable,
    setSizeChart,
    setDisabled,
  }
}

export { useSizeLineHook, separatorSign }
